import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Button, Form, Col, Row, InputGroup, Collapse } from 'react-bootstrap';
import AsyncSelect from 'react-select/async';
import axios from 'axios';
import { toast } from 'react-toastify';
import _ from 'lodash';

const ProductModal = ({ show, handleClose, refreshProducts, product = null, initialProductName = '' }) => {
    const [formState, setFormState] = useState({
        productName: '',
        description: '',
        selectedBrand: null,
        selectedCategory: null,
        isOwner: false,
        measureType: '',
        weight: '',
        volume: '',
        units: '',
        tags: '',
        websiteUrl: '',
        showMoreDetails: false,
        barcode: '',
        gtin: '',
        upc: '',
        ean: ''
    });

    const token = localStorage.getItem('token');

    // States to store options
    const [brandOptions, setBrandOptions] = useState([]);
    const [categoryOptions, setCategoryOptions] = useState([]);
    
    // Inline brand creation state
    const [showInlineBrandForm, setShowInlineBrandForm] = useState(false);
    const [newBrandName, setNewBrandName] = useState('');
    const [newBrandDescription, setNewBrandDescription] = useState('');
    const [newBrandWebsite, setNewBrandWebsite] = useState('');
    const [creatingBrand, setCreatingBrand] = useState(false);

    const resetForm = useCallback(() => {
        setFormState({
            productName: initialProductName || '',
            description: '',
            selectedBrand: null,
            selectedCategory: null,
            isOwner: false,
            measureType: '',
            weight: '',
            volume: '',
            units: '',
            tags: '',
            websiteUrl: '',
            showMoreDetails: false,
            barcode: '',
            gtin: '',
            upc: '',
            ean: ''
        });
    }, [initialProductName]);

    const fetchInitialBrandsAndCategories = useCallback(async () => {
        try {
            const [brandsResponse, categoriesResponse] = await Promise.all([
                axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/highgarden/grocery-product-brands/`, {
                    params: { page_size: 10 },
                    headers: { 'Authorization': `Token ${token}` },
                }),
                axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/highgarden/grocery-product-categories/`, {
                    params: { page_size: 10 },
                    headers: { 'Authorization': `Token ${token}` },
                })
            ]);

            setBrandOptions(brandsResponse.data.results.map(brand => ({
                label: brand.name,
                value: brand.id
            })));

            setCategoryOptions(categoriesResponse.data.results.map(category => ({
                label: category.name,
                value: category.id
            })));
        } catch (error) {
            console.error('Failed to fetch initial brands and categories:', error);
        }
    }, [token]);

    useEffect(() => {
        if (product) {
            // Determine measureType and values based on measurement_type
            let measureType = '';
            let weight = '';
            let volume = '';
            let units = '';

            if (product.measurement_type === 'weight') {
                measureType = 'weight';
                weight = product.measure || '';
            } else if (product.measurement_type === 'volume') {
                measureType = 'volume';
                volume = product.measure || '';
            } else if (product.measurement_type === 'units') {
                measureType = 'units';
                units = product.measure || '';
            }

            setFormState({
                productName: product.name || '',
                description: product.description || '',
                selectedBrand: { value: product.product_brand.id, label: product.product_brand.name },
                selectedCategory: { value: product.category.id, label: product.category.name },
                isOwner: product.is_owner,
                measureType,
                weight,
                volume,
                units,
                tags: product.tags ? product.tags.join(', ') : '',
                websiteUrl: product.website_url || '',
                showMoreDetails: false,
                barcode: product.barcode || '',
                gtin: product.gtin || '',
                upc: product.upc || '',
                ean: product.ean || ''
            });
        } else {
            resetForm();
        }

        fetchInitialBrandsAndCategories();
    }, [product, initialProductName, resetForm, fetchInitialBrandsAndCategories]);

    const handleInputChange = (field, value) => {
        setFormState(prevState => ({ ...prevState, [field]: value }));
    };

    const formatWebsiteUrl = (url) => {
        if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
            return `https://${url}`;
        }
        return url;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const {
            productName, selectedBrand, selectedCategory, description,
            websiteUrl, tags, measureType, weight, volume, units,
            barcode, gtin, upc, ean
        } = formState;

        if (!productName.trim() || !selectedBrand || !selectedCategory) {
            toast.error('Product name, category, and brand are required.');
            return;
        }

        // Determine measurement_type and unit_of_measure based on measure type
        let measurement_type = null;
        let measure = null;
        let unit_of_measure = null;

        if (measureType === 'weight' && weight) {
            measurement_type = 'weight';
            measure = parseFloat(weight);
            unit_of_measure = 'g';
        } else if (measureType === 'volume' && volume) {
            measurement_type = 'volume';
            measure = parseFloat(volume);
            unit_of_measure = 'mL';
        } else if (measureType === 'units' && units) {
            measurement_type = 'units';
            measure = parseFloat(units);
            unit_of_measure = 'pcs';
        }

        const payload = {
            name: productName,
            product_brand_id: selectedBrand.value,
            category_id: selectedCategory.value,
            description: description || undefined,
            website_url: formatWebsiteUrl(websiteUrl) || undefined,
            measurement_type,
            measure,
            unit_of_measure,
            tags: tags.trim() ? tags.split(',').map(tag => tag.trim()) : undefined,
            barcode: barcode.trim() || undefined,
            gtin: gtin.trim() || undefined,
            upc: upc.trim() || undefined,
            ean: ean.trim() || undefined
        };

        try {
            if (product && formState.isOwner) {
                await axios.patch(`${process.env.REACT_APP_BACKEND_API_URL}/highgarden/grocery-products/${product.id}/`, payload, {
                    headers: { 'Authorization': `Token ${token}` },
                });
                refreshProducts();
                toast.success('Product updated successfully!');
            } else {
                await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/highgarden/grocery-products/`, payload, {
                    headers: { 'Authorization': `Token ${token}` },
                });
                toast.success('Product added successfully!');
            }
            handleClose();
        } catch (error) {
            console.error('Error submitting product:', error.response ? error.response.data : 'No response data');
            toast.error('Failed to submit product. Please try again.');
        }
    };

    const handleDelete = async () => {
        try {
            await axios.delete(`${process.env.REACT_APP_BACKEND_API_URL}/highgarden/grocery-products/${product.id}/`, {
                headers: { 'Authorization': `Token ${token}` }
            });
            toast.success('Product deleted successfully!');
            handleClose();
            refreshProducts();
        } catch (error) {
            console.error('Error deleting product:', error);
            toast.error('Failed to delete product. Please try again.');
        }
    };

    const debouncedLoadOptions = (url) => _.debounce(async (inputValue, callback) => {
        try {
            const response = await axios.get(url, {
                params: { search: inputValue, page_size: 10 },
                headers: { 'Authorization': `Token ${token}` }
            });
            callback(response.data.results.map(option => ({
                label: option.name,
                value: option.id
            })));
        } catch (error) {
            console.error(`Error loading options from ${url}:`, error);
            callback([]);
        }
    }, 300);

    // Load brand options with "Add Brand" option when no results
    const loadBrandOptions = async (inputValue, callback) => {
        // If no input, return default options
        if (!inputValue || inputValue.length < 2) {
            callback([]);
            return;
        }

        try {
            const response = await axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/highgarden/grocery-product-brands/`, {
                params: { search: inputValue, page_size: 10 },
                headers: { 'Authorization': `Token ${token}` }
            });
            
            const brandOptions = response.data.results.map(option => ({
                label: option.name,
                value: option.id,
                isBrand: true
            }));

            // If no results found, add "Add Brand" option
            if (brandOptions.length === 0) {
                callback([{
                    value: 'add-new-brand',
                    label: `➕ Add "${inputValue}" as new brand`,
                    isAddNew: true,
                    brandName: inputValue
                }]);
            } else {
                callback(brandOptions);
            }
        } catch (error) {
            console.error('Error loading brands:', error);
            callback([]);
        }
    };

    const debouncedLoadBrandOptions = useRef(
        _.debounce((inputValue, callback) => {
            loadBrandOptions(inputValue, callback);
        }, 300)
    ).current;

    const loadCategoryOptions = debouncedLoadOptions(`${process.env.REACT_APP_BACKEND_API_URL}/highgarden/grocery-product-categories/`);

    // Create new brand inline
    const handleCreateBrand = async () => {
        if (!newBrandName.trim() || !newBrandDescription.trim()) {
            toast.error('Brand name and description are required.');
            return;
        }

        setCreatingBrand(true);
        try {
            const formattedWebsite = newBrandWebsite && !newBrandWebsite.startsWith('http://') && !newBrandWebsite.startsWith('https://') 
                ? `https://${newBrandWebsite}` 
                : newBrandWebsite;

            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_API_URL}/highgarden/grocery-product-brands/`,
                {
                    name: newBrandName,
                    description: newBrandDescription,
                    website: formattedWebsite || undefined,
                    status: 'ACTIVE'
                },
                {
                    headers: { 'Authorization': `Token ${token}` }
                }
            );

            const newBrand = {
                label: response.data.name,
                value: response.data.id,
                isBrand: true
            };

            // Set the newly created brand as selected
            setFormState(prevState => ({ ...prevState, selectedBrand: newBrand }));
            setBrandOptions(prev => [...prev, newBrand]);

            // Clear inline form
            setNewBrandName('');
            setNewBrandDescription('');
            setNewBrandWebsite('');
            setShowInlineBrandForm(false);

            toast.success('Brand created successfully!');
        } catch (error) {
            console.error('Error creating brand:', error);
            toast.error('Failed to create brand. Please try again.');
        } finally {
            setCreatingBrand(false);
        }
    };

    // Handle brand selection
    const handleBrandChange = (selected) => {
        if (selected && selected.isAddNew) {
            // User selected "Add Brand" option
            setNewBrandName(selected.brandName || '');
            setShowInlineBrandForm(true);
            // Don't set the brand in the form yet
        } else {
            // Normal brand selection
            handleInputChange('selectedBrand', selected);
        }
    };

    // Refresh the favorite stores list in parent component
    const handleAddFavorite = async (productId) => {
        try {
            await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/highgarden/user/favorite-products/`, {
                product_id: productId
            }, {
                headers: {
                    'Authorization': `Token ${localStorage.getItem('token')}`
                }
            });
            toast.success("Product added to favorites successfully!");
        } catch (error) {
            toast.error("Error adding product to favorites!");
            console.error("Error adding favorite product:", error);
        }
    };

    return (
        <Modal show={show} onHide={handleClose}>
            <Modal.Header closeButton>
                <Modal.Title>{product ? product.name : 'Add New Product'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={handleSubmit}>
                    <Row>
                        <Col>
                            <Form.Group className="mb-3">
                                <Form.Label>Brand</Form.Label>
                                <AsyncSelect
                                    cacheOptions={false}
                                    defaultOptions={brandOptions}
                                    loadOptions={debouncedLoadBrandOptions}
                                    onChange={handleBrandChange}
                                    value={formState.selectedBrand}
                                    isDisabled={!!product && !formState.isOwner}
                                    placeholder="Search for a brand..."
                                    noOptionsMessage={({ inputValue }) => 
                                        !inputValue || inputValue.length < 2 
                                            ? "Type at least 2 characters" 
                                            : "No brands found - type to search"
                                    }
                                />
                            </Form.Group>

                            {/* Inline Brand Creation Form */}
                            <Collapse in={showInlineBrandForm}>
                                <div className="mb-3 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '5px', border: '1px solid #dee2e6' }}>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <h6 className="mb-0">Add New Brand</h6>
                                        <Button 
                                            variant="link" 
                                            size="sm" 
                                            className="p-0 text-muted"
                                            onClick={() => {
                                                setShowInlineBrandForm(false);
                                                setNewBrandName('');
                                                setNewBrandDescription('');
                                                setNewBrandWebsite('');
                                            }}
                                        >
                                            ✕ Cancel
                                        </Button>
                                    </div>
                                    <Form.Group className="mb-2">
                                        <Form.Label>Brand Name *</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter brand name"
                                            value={newBrandName}
                                            onChange={(e) => setNewBrandName(e.target.value)}
                                        />
                                    </Form.Group>
                                    <Form.Group className="mb-2">
                                        <Form.Label>Description *</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={2}
                                            placeholder="Enter brand description"
                                            value={newBrandDescription}
                                            onChange={(e) => setNewBrandDescription(e.target.value)}
                                        />
                                    </Form.Group>
                                    <Form.Group className="mb-2">
                                        <Form.Label>Website (optional)</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter brand website"
                                            value={newBrandWebsite}
                                            onChange={(e) => setNewBrandWebsite(e.target.value)}
                                        />
                                    </Form.Group>
                                    <Button 
                                        variant="success" 
                                        size="sm"
                                        onClick={handleCreateBrand}
                                        disabled={creatingBrand}
                                    >
                                        {creatingBrand ? 'Creating...' : 'Create Brand'}
                                    </Button>
                                </div>
                            </Collapse>
                        </Col>
                        <Col>
                            <Form.Group className="mb-3">
                                <Form.Label>Category</Form.Label>
                                <AsyncSelect
                                    cacheOptions
                                    defaultOptions={categoryOptions}
                                    loadOptions={loadCategoryOptions}
                                    onChange={(selected) => handleInputChange('selectedCategory', selected)}
                                    value={formState.selectedCategory}
                                    isDisabled={!!product && !formState.isOwner}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Form.Group className="mb-3">
                                <Form.Label>Product Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter product name"
                                    value={formState.productName}
                                    onChange={(e) => handleInputChange('productName', e.target.value)}
                                    disabled={!!product && !formState.isOwner}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Form.Group className="mb-3">
                                <Form.Label>Description</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    placeholder="Enter product description"
                                    value={formState.description}
                                    onChange={(e) => handleInputChange('description', e.target.value)}
                                    disabled={!!product && !formState.isOwner}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Form.Group className="mb-3">
                                <Form.Label>Website URL</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter product website URL"
                                    value={formState.websiteUrl}
                                    onChange={(e) => handleInputChange('websiteUrl', e.target.value)}
                                    disabled={!!product && !formState.isOwner}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Form.Group className="mb-3">
                                <Form.Label>Tags</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter tags, separated by commas"
                                    value={formState.tags}
                                    onChange={(e) => handleInputChange('tags', e.target.value)}
                                    disabled={!!product && !formState.isOwner}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Form.Group className="mb-3">
                                <Form.Label>Measure Type</Form.Label>
                                <Form.Control
                                    as="select"
                                    value={formState.measureType}
                                    onChange={(e) => handleInputChange('measureType', e.target.value)}
                                    disabled={!!product && !formState.isOwner}
                                >
                                    <option value="">Select a measure type</option>
                                    <option value="weight">Weight</option>
                                    <option value="volume">Volume</option>
                                    <option value="units">Units</option>
                                </Form.Control>
                            </Form.Group>
                        </Col>
                        <Col>
                            {formState.measureType === 'weight' && (
                                <Form.Group className="mb-3">
                                    <Form.Label>Weight (grams)</Form.Label>
                                    <InputGroup>
                                        <Form.Control
                                            type="number"
                                            placeholder="Enter product weight"
                                            value={formState.weight}
                                            onChange={(e) => handleInputChange('weight', e.target.value)}
                                            disabled={!!product && !formState.isOwner}
                                        />
                                        <InputGroup.Text>g</InputGroup.Text>
                                    </InputGroup>
                                </Form.Group>
                            )}
                            {formState.measureType === 'volume' && (
                                <Form.Group className="mb-3">
                                    <Form.Label>Volume (milliliters)</Form.Label>
                                    <InputGroup>
                                        <Form.Control
                                            type="number"
                                            placeholder="Enter product volume"
                                            value={formState.volume}
                                            onChange={(e) => handleInputChange('volume', e.target.value)}
                                            disabled={!!product && !formState.isOwner}
                                        />
                                        <InputGroup.Text>mL</InputGroup.Text>
                                    </InputGroup>
                                </Form.Group>
                            )}
                            {formState.measureType === 'units' && (
                                <Form.Group className="mb-3">
                                    <Form.Label>Units</Form.Label>
                                    <Form.Control
                                        type="number"
                                        placeholder="Enter product units"
                                        value={formState.units}
                                        onChange={(e) => handleInputChange('units', e.target.value)}
                                        disabled={!!product && !formState.isOwner}
                                    />
                                </Form.Group>
                            )}
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Button
                                variant="link"
                                onClick={() => handleInputChange('showMoreDetails', !formState.showMoreDetails)}
                                aria-controls="collapse-details"
                                aria-expanded={formState.showMoreDetails}
                            >
                                {formState.showMoreDetails ? 'Hide Details' : 'Show Details'}
                            </Button>
                        </Col>
                    </Row>
                    <Collapse in={formState.showMoreDetails}>
                        <div id="collapse-details">
                            <Row>
                                <Col>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Barcode</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter product barcode"
                                            value={formState.barcode}
                                            onChange={(e) => handleInputChange('barcode', e.target.value)}
                                            disabled={!!product && !formState.isOwner}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Group className="mb-3">
                                        <Form.Label>GTIN</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter product GTIN"
                                            value={formState.gtin}
                                            onChange={(e) => handleInputChange('gtin', e.target.value)}
                                            disabled={!!product && !formState.isOwner}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    <Form.Group className="mb-3">
                                        <Form.Label>UPC</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter product UPC"
                                            value={formState.upc}
                                            onChange={(e) => handleInputChange('upc', e.target.value)}
                                            disabled={!!product && !formState.isOwner}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Group className="mb-3">
                                        <Form.Label>EAN</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter product EAN"
                                            value={formState.ean}
                                            onChange={(e) => handleInputChange('ean', e.target.value)}
                                            disabled={!!product && !formState.isOwner}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </div>
                    </Collapse>
                    {!product && (
                        <Button variant="primary" type="submit">
                            Add Product
                        </Button>
                    )}
                    {product && formState.isOwner && (
                        <>
                            <Button variant="primary" type="submit">
                                Update Product
                            </Button>
                            <Button variant="danger" onClick={handleDelete} style={{ marginLeft: '10px' }}>
                                Delete Product
                            </Button>
                        </>
                    )}
                    {product && (
                        <Button
                            variant="primary"
                            onClick={() => handleAddFavorite(product.id)}
                            style={{ marginLeft: '10px' }}
                        >
                            Add to Favorites
                        </Button>
                    )}
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default ProductModal;
